import {
  Lci3SalesMetrics,
  Subdimension,
  SubdimensionVisitMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import {
  faDollar,
  faEye,
  faShop,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ValueInput, formatters } from '@utils/formattersV2';
import { ColumnHeader } from '@features/campaigns';
import {
  OptimizationSelect,
  OptimizationSelectProps,
  Option as BaseOption,
} from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelect';
import { StatisticallySignificant } from './StatisticallySignificant';
import { ReactNode } from 'react';
import { OverflowText } from '@components/shared/OverflowText';
import { OptimizationIndexChip } from '@features/campaigns/components/OptimizationIndexChip';

const liftFormatter = (lift: ValueInput, confidenceLevel: ValueInput) =>
  `${formatters.lift(lift)} (${formatters.confidenceLevel(
    confidenceLevel,
    lift
  )} confidence)`;

const salesLiftFormatter = (value: ValueInput, row: RowModel) =>
  liftFormatter(value, row.salesMetrics?.confidenceLevel.total);

const visitLiftFormatter = (value: ValueInput, row: RowModel) =>
  liftFormatter(value, row.visitMetrics.confidenceLevel);

export type RowModel = Subdimension;

export const renderHeaderSales = ({
  colDef,
}: {
  colDef: GridColDef<RowModel>;
}) => (
  <ColumnHeader
    icon={faDollar}
    headerName={colDef.headerName ?? ''}
    color="sales"
  />
);

const renderHeaderVisit = ({ colDef }: { colDef: GridColDef<RowModel> }) => (
  <ColumnHeader
    icon={faShop}
    headerName={colDef.headerName ?? ''}
    color="visit"
  />
);

export const renderCellStatisticallySignificant: GridColDef<RowModel>['renderCell'] =
  ({ formattedValue, row }: { formattedValue?: ReactNode; row: RowModel }) => (
    <StatisticallySignificant row={row}>
      {formattedValue}
    </StatisticallySignificant>
  );

export const optimizationIndexOptions = {
  incrementalVisitLiftPercentageIndex: {
    name: 'Opt. Index - Incr. Visit Lift',
    value: 'incrementalVisitLiftPercentageIndex',
    source: 'visitMetrics',
  },
  incrementalVisitsPerImpressionIndex: {
    name: 'Opt. Index - Incr. Visits per Impression',
    value: 'incrementalVisitsPerImpressionIndex',
    source: 'visitMetrics',
  },
  totalVisitsPerImpressionIndex: {
    name: 'Opt. Index - Total Visits per Impression',
    value: 'totalVisitsPerImpressionIndex',
    source: 'visitMetrics',
  },
  incrementalSalesLiftPercentageIndex: {
    name: 'Opt. Index - Incr. Sales Lift',
    value: 'incrementalSalesLiftPercentageIndex',
    source: 'salesMetrics',
  },
  incrementalSalesPerImpressionIndex: {
    name: 'Opt. Index - Incr. Sales per Impression',
    value: 'incrementalSalesPerImpressionIndex',
    source: 'salesMetrics',
  },
  totalSalesPerImpressionIndex: {
    name: 'Opt. Index - Total Sales per Impression',
    value: 'totalSalesPerImpressionIndex',
    source: 'salesMetrics',
  },
} as const satisfies Record<Option['value'], Option>;

export const nameColumn: GridColDef<Subdimension> = {
  field: 'dimension',
  headerName: 'Dimension',
  display: 'flex',
  flex: 1,
  valueGetter: (_, row) => row.value,
  renderCell: ({ row }) => (
    <Tooltip title={row.value} placement="top">
      <OverflowText>{row.value}</OverflowText>
    </Tooltip>
  ),
};

export type Option = BaseOption &
  (
    | {
        source: 'salesMetrics';
        value: keyof Pick<
          Lci3SalesMetrics,
          | 'incrementalSalesLiftPercentageIndex'
          | 'incrementalSalesPerImpressionIndex'
          | 'totalSalesPerImpressionIndex'
        >;
      }
    | {
        source: 'visitMetrics';
        value: keyof Pick<
          Subdimension['visitMetrics'],
          | 'incrementalVisitLiftPercentageIndex'
          | 'totalVisitsPerImpressionIndex'
          | 'incrementalVisitsPerImpressionIndex'
        >;
      }
  );

export type ColumnsOptions = {
  selectedOptimization: Option;
  setSelectedOptimization: OptimizationSelectProps<Option>['setSelectedOptimization'];
  showSalesColumns?: boolean;
};

export const columns = (
  options: ColumnsOptions
): GridColDef<Subdimension, ValueInput | string>[] => {
  const { selectedOptimization, setSelectedOptimization, showSalesColumns } =
    options;

  const baseColumns: GridColDef<Subdimension, ValueInput | string>[] = [
    nameColumn,
    {
      field: 'optimizationIndex',
      headerName: selectedOptimization.name,
      flex: 1,
      valueGetter: (_, row) => {
        if (selectedOptimization.source === 'visitMetrics') {
          return row.visitMetrics[selectedOptimization.value];
        }
        return row.salesMetrics?.[selectedOptimization.value].total;
      },
      valueFormatter: value =>
        formatters.number(value as ValueInput, { decimals: 2 }),
      renderHeader: () => (
        <OptimizationSelect
          selectedOptimization={selectedOptimization}
          setSelectedOptimization={setSelectedOptimization}
          options={Object.values(optimizationIndexOptions)}
        />
      ),
      renderCell: ({ value, row }) => (
        <StatisticallySignificant row={row}>
          <OptimizationIndexChip value={value as ValueInput} />
        </StatisticallySignificant>
      ),
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      flex: 1,
      valueGetter: (_, row) => row.visitMetrics.impressions,
      valueFormatter: value =>
        formatters.impressions(
          value as SubdimensionVisitMetrics['impressions'],
          { full: true }
        ),
      renderHeader: ({ colDef }) => (
        <ColumnHeader
          icon={faEye}
          headerName={colDef.headerName ?? ''}
          color="plain"
        />
      ),
    },
    {
      field: 'uniqueUsers',
      headerName: 'Unique Users',
      flex: 1,
      valueGetter: (_, row) => row.visitMetrics.uniqueUsers,
      valueFormatter: value =>
        formatters.users(value as SubdimensionVisitMetrics['uniqueUsers'], {
          full: true,
        }),
      renderHeader: ({ colDef }) => (
        <ColumnHeader
          icon={faUser}
          headerName={colDef.headerName ?? ''}
          color="plain"
        />
      ),
    },
    {
      field: 'projectedVisits',
      headerName: 'Projected Visits',
      flex: 1,
      valueGetter: (_, row) => row.visitMetrics.projectedVisits,
      valueFormatter: value =>
        formatters.visits(
          value as SubdimensionVisitMetrics['projectedVisits'],
          { full: true }
        ),
      renderHeader: renderHeaderVisit,
    },
    {
      field: 'incrementalLift',
      headerName: 'Incr. Visit Lift',
      flex: 1.5,
      valueGetter: (_, row) => row.visitMetrics.incrementalLift,
      valueFormatter: (value, row) => visitLiftFormatter(value, row),
      renderHeader: renderHeaderVisit,
      renderCell: renderCellStatisticallySignificant,
    },
    {
      field: 'incrementalVisits',
      headerName: 'Incr. Visits',
      flex: 1,
      valueGetter: (_, row) => row.visitMetrics.incrementalVisits,
      valueFormatter: value =>
        formatters.visits(
          value as SubdimensionVisitMetrics['incrementalVisits'],
          { full: true }
        ),
      renderHeader: renderHeaderVisit,
      renderCell: renderCellStatisticallySignificant,
    },
  ];

  if (!showSalesColumns) {
    return baseColumns;
  }

  return [
    ...baseColumns,
    {
      field: 'incrementalSalesLift',
      headerName: 'Incr. Sales Lift',
      flex: 1.5,
      valueGetter: (_, row) => row.salesMetrics?.incrementalSalesLift.total,
      valueFormatter: (value, row) => salesLiftFormatter(value, row),
      renderHeader: renderHeaderSales,
      renderCell: renderCellStatisticallySignificant,
    },
    {
      field: 'totalSales',
      headerName: 'Total Sales',
      flex: 1,
      valueGetter: (_, row) => row.salesMetrics?.totalProjectedSales.total,
      valueFormatter: value => formatters.currency(value as ValueInput),
      renderHeader: renderHeaderSales,
      renderCell: renderCellStatisticallySignificant,
    },
    {
      field: 'incrementalSales',
      headerName: 'Incr. Sales',
      flex: 1,
      valueGetter: (_, row) => row.salesMetrics?.incrementalSales.total,
      valueFormatter: value => formatters.currency(value as ValueInput),
      renderHeader: renderHeaderSales,
      renderCell: renderCellStatisticallySignificant,
    },
  ];
};
